import PropTypes from 'prop-types';
import { getIn, useFormikContext } from 'formik';
import {
  Autocomplete,
  Grid,
  TextField
} from '@mui/material';
import { CountryRegionData } from 'react-country-region-selector';

const COUNTRY_REGION_DATA = CountryRegionData.default || CountryRegionData;

const isValueEmpty = (value) => {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
};

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  helperText,
  required,
  requiredHintText,
  disabled = false
}) => {
  const renderHelperText = () => {
    if (error) return helperText;
    if (required && isValueEmpty(value)) return requiredHintText;
    return '';
  };

  return (
    <Autocomplete
      fullWidth
      options={options || []}
      value={options?.find((option) => option.value === value) || null}
      disabled={disabled}
      isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
      onChange={(_, selectedOption) => onChange(selectedOption?.value || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={Boolean(error)}
          helperText={renderHelperText()}
          onBlur={onBlur}
        />
      )}
    />
  );
};

SearchableSelect.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  requiredHintText: PropTypes.string,
  disabled: PropTypes.bool
};

SearchableSelect.defaultProps = {
  options: [],
  value: '',
  onBlur: undefined,
  placeholder: '',
  error: false,
  helperText: '',
  required: false,
  requiredHintText: 'This field is required',
  disabled: false
};

const CountryStateFields = ({
  countryField,
  stateField,
  requiredHintText
}) => {
  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleBlur
  } = useFormikContext();

  const countryValue = getIn(values, countryField.name) || '';
  const stateValue = getIn(values, stateField.name) || '';
  const countryError = Boolean(getIn(touched, countryField.name) && getIn(errors, countryField.name));
  const stateError = Boolean(getIn(touched, stateField.name) && getIn(errors, stateField.name));
  const countryOptions = COUNTRY_REGION_DATA.map(([name, code]) => ({
    value: code,
    label: name
  }));
  const selectedCountry = COUNTRY_REGION_DATA.find(([, code]) => code === countryValue);
  const stateOptions = selectedCountry
    ? selectedCountry[2].split('|').filter(Boolean).map((state) => {
      const [label, value = label] = state.split('~');
      return { value, label };
    })
    : [];

  return (
    <>
      <Grid item xs={12} sm={6}>
        <SearchableSelect
          label={countryField.label}
          options={countryOptions}
          value={countryValue}
          placeholder="Search country"
          error={countryError}
          helperText={getIn(errors, countryField.name)}
          required
          requiredHintText={requiredHintText}
          onBlur={handleBlur}
          onChange={(value) => {
            setFieldValue(countryField.name, value);
            if (stateValue) setFieldValue(stateField.name, '');
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SearchableSelect
          label={stateField.label}
          options={stateOptions}
          value={stateValue}
          placeholder="Search state"
          error={stateError}
          helperText={getIn(errors, stateField.name)}
          required
          requiredHintText={requiredHintText}
          disabled={!countryValue}
          onBlur={handleBlur}
          onChange={(value) => setFieldValue(stateField.name, value)}
        />
      </Grid>
    </>
  );
};

CountryStateFields.propTypes = {
  countryField: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired,
  stateField: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired,
  requiredHintText: PropTypes.string
};

CountryStateFields.defaultProps = {
  requiredHintText: 'This field is required'
};

export default CountryStateFields;
